import styled from "styled-components";
import { MediaQuery } from "../../hooks/useDeviceType";
import {
  GAINSBORO,
  GHOST_WHITE,
  NORMAL_HEIGHT,
  NORMAL_WEIGHT,
  PRIMARY_FONT,
  SECONDARY_FONT,
  SECONDARY_SIZE,
  SLIM_WEIGHT,
  TITLE_SIZE,
} from "../../utils/variables";

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 110rem;
  margin: 0 auto;
  padding: 0 12rem;
  min-height: 100vh;
  padding-top: 12rem;
  padding-bottom: 8rem;
  position: relative;
  isolation: isolate;

  @media ${MediaQuery.isTablet} {
    padding: 11rem 6rem 7rem;
  }

  @media ${MediaQuery.isMobile} {
    padding: 10rem 3rem 6rem;
  }
`;

export const SpecialText = styled.p`
  font-family: ${SECONDARY_FONT};
  font-size: ${SECONDARY_SIZE};
  margin-bottom: 3rem;
`;

export const Title = styled.h1`
  font-family: ${PRIMARY_FONT};
  font-size: ${TITLE_SIZE};
  line-height: 8rem;
  color: ${GHOST_WHITE};

  @media ${MediaQuery.isTablet} {
    font-size: 4.2rem;
    line-height: 5.1rem;
  }

  @media ${MediaQuery.isMobile} {
    line-height: 4rem;
    font-size: 2.8rem;
  }
`;

export const Heading = styled.h2`
  font-family: ${PRIMARY_FONT};
  font-size: 5.8rem;
  line-height: 7rem;

  @media ${MediaQuery.isTablet} {
    font-size: 4rem;
    line-height: 4.9rem;
  }

  @media ${MediaQuery.isMobile} {
    line-height: 3.6rem;
    font-size: 2.6rem;
  }
`;

export const Text = styled.p`
  margin-top: 2rem;
  max-width: 75%;
  color: ${GAINSBORO};
  font-weight: ${SLIM_WEIGHT};
  font-size: ${SECONDARY_SIZE};
  line-height: ${NORMAL_HEIGHT};

  @media ${MediaQuery.isTablet} {
    max-width: 80%;
  }

  @media ${MediaQuery.isMobile} {
    max-width: 100%;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`;

export const HeroBackground = styled.div`
  --pointer-x: 50%;
  --pointer-y: 42%;
  --pointer-opacity: 0.18;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 100vw;
  overflow: hidden;
  pointer-events: none;
  transform: translateX(-50%);
  background:
    radial-gradient(
      ellipse at 12% 24%,
      rgba(76, 106, 145, 0.3),
      transparent 38%
    ),
    radial-gradient(
      ellipse at 88% 18%,
      rgba(55, 126, 112, 0.24),
      transparent 34%
    ),
    radial-gradient(
      ellipse at 60% 88%,
      rgba(104, 79, 137, 0.2),
      transparent 38%
    );
  background-size: 130% 130%;
  animation: hero-aurora 8s ease-in-out infinite alternate;

  span {
    position: absolute;
    display: block;
    border-radius: 50%;
    filter: blur(5rem);
    mix-blend-mode: screen;
    will-change: transform, opacity;
  }

  span:nth-child(1) {
    width: 34rem;
    height: 34rem;
    top: 10%;
    left: 4%;
    opacity: 0.48;
    background: rgba(70, 108, 158, 0.48);
    animation: hero-float-one 7s ease-in-out infinite alternate;
  }

  span:nth-child(2) {
    width: 30rem;
    height: 30rem;
    top: 18%;
    right: 5%;
    opacity: 0.42;
    background: rgba(48, 128, 111, 0.44);
    animation: hero-float-two 9s ease-in-out infinite alternate;
  }

  span:nth-child(3) {
    width: 28rem;
    height: 28rem;
    bottom: -8%;
    left: 42%;
    opacity: 0.38;
    background: rgba(109, 74, 143, 0.44);
    animation: hero-float-three 11s ease-in-out infinite alternate;
  }

  &::before {
    content: "";
    position: absolute;
    width: 58rem;
    height: 58rem;
    top: 8%;
    right: -8%;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(85, 132, 158, 0.26),
      rgba(65, 110, 125, 0.1) 42%,
      transparent 70%
    );
    filter: blur(2rem);
    animation: hero-orb 7s ease-in-out infinite alternate;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.4;
    background-image: radial-gradient(
      circle,
      rgba(250, 250, 255, 0.2) 1px,
      transparent 1px
    );
    background-size: 4rem 4rem;
    -webkit-mask-image: linear-gradient(
      110deg,
      transparent 10%,
      black 45%,
      transparent 82%
    );
    mask-image: linear-gradient(
      110deg,
      transparent 10%,
      black 45%,
      transparent 82%
    );
    animation: hero-particles 11s linear infinite;
  }

  @keyframes hero-aurora {
    0% {
      background-position: 0% 20%;
    }
    50% {
      background-position: 50% 65%;
    }
    100% {
      background-position: 100% 35%;
    }
  }

  @keyframes hero-orb {
    from {
      transform: translate3d(-4%, -3%, 0) scale(0.95);
    }
    to {
      transform: translate3d(-18%, 12%, 0) scale(1.12);
    }
  }

  @keyframes hero-float-one {
    from {
      transform: translate3d(-4rem, -2rem, 0) scale(0.9);
      opacity: 0.26;
    }
    to {
      transform: translate3d(16rem, 7rem, 0) scale(1.14);
      opacity: 0.48;
    }
  }

  @keyframes hero-float-two {
    from {
      transform: translate3d(5rem, -3rem, 0) scale(1.08);
      opacity: 0.24;
    }
    to {
      transform: translate3d(-15rem, 10rem, 0) scale(0.92);
      opacity: 0.42;
    }
  }

  @keyframes hero-float-three {
    from {
      transform: translate3d(-7rem, 5rem, 0) scale(0.88);
      opacity: 0.2;
    }
    to {
      transform: translate3d(12rem, -10rem, 0) scale(1.16);
      opacity: 0.38;
    }
  }

  @keyframes hero-particles {
    from {
      transform: translate3d(0, 0, 0);
    }
    to {
      transform: translate3d(4rem, 4rem, 0);
    }
  }
`;

export const PointerGlow = styled.div`
  position: absolute;
  left: var(--pointer-x);
  top: var(--pointer-y);
  width: 54rem;
  height: 54rem;
  border-radius: 50%;
  opacity: var(--pointer-opacity);
  transform: translate3d(-50%, -50%, 0);
  background: radial-gradient(
    circle,
    rgba(220, 240, 255, 0.3) 0%,
    rgba(91, 154, 182, 0.17) 24%,
    rgba(55, 126, 112, 0.08) 48%,
    transparent 70%
  );
  filter: blur(2.5rem);
  mix-blend-mode: screen;
  will-change: left, top, opacity;
  transition: left 120ms ease-out, top 120ms ease-out, opacity 240ms ease;

  @media ${MediaQuery.isMobile} {
    display: none;
  }
`;

export const Technologies = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  max-width: 60rem;
  margin-top: 3rem;
  list-style: none;
`;

export const Technology = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.8rem;
  height: 3.8rem;
  color: ${(props) => (props.$active ? GHOST_WHITE : GAINSBORO)};
  border: 1px solid
    ${(props) =>
      props.$active
        ? "rgba(250, 250, 255, 0.55)"
        : "rgba(218, 221, 216, 0.2)"};
  border-radius: 0.7rem;
  background-color: ${(props) =>
    props.$active
      ? "rgba(255, 255, 255, 0.07)"
      : "rgba(255, 255, 255, 0.025)"};
  font: inherit;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease;

  svg {
    width: 2rem;
    height: 2rem;
  }

  &:hover {
    color: ${GHOST_WHITE};
    border-color: rgba(250, 250, 255, 0.5);
    background-color: rgba(255, 255, 255, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${GHOST_WHITE};
    outline-offset: 0.3rem;
  }

  @media ${MediaQuery.isMobile} {
    display: inline-flex;
    width: 3.5rem;
    height: 3.5rem;
    font-family: ${SECONDARY_FONT};
    font-size: 1.2rem;
    font-weight: ${NORMAL_WEIGHT};

    svg {
      width: 1.8rem;
      height: 1.8rem;
    }
  }
`;

export const TechnologyName = styled.p`
  min-height: 1.8rem;
  margin-top: 1rem;
  color: ${GAINSBORO};
  font-family: ${SECONDARY_FONT};
  font-size: 1.2rem;
`;

export const Link = styled.a`
  text-decoration: none;
  font-weight: ${NORMAL_WEIGHT};
  color: ${GHOST_WHITE};
  display: inline-block;
  position: relative;

  &:hover::after {
    transform: scaleX(1);
    transform-origin: bottom left;
  }

  &::after {
    content: "";
    position: absolute;
    width: 100%;
    transform: scaleX(0);
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: ${GHOST_WHITE};
    transform-origin: bottom right;
    transition: transform 0.25s ease-out;
  }
`;
